import React, { useEffect, useRef, useState } from 'react'
import { FieldNames } from 'Constants/FieldNames'
import { UISizes } from 'LibNeo'
import { Validators, fmsSelectors, fmsSlice } from 'Modules'
import { WrappedFormUtils } from 'antd/lib/form/Form'
import { useSelector } from 'react-redux'
import { useAppDispatch, useEffectOnce } from 'Src/hooks'
import { INITIATOR_REQUEST_RBIDOS_PAID } from 'Constants/Address.constants'
import {
	BgFormRowElement,
	BgSelectWithOther,
	getPassportDataText,
	isAuthPassport,
} from 'Pages/ibro'
import { AuthPassportKodWrapper } from './styled'

const formatProps = { format: '###-###', mask: '_', placeholder: '___-___' }

interface BaseItem {
	locked_fields: FieldNames[]
	required_fields: FieldNames[]
	hidden_fields?: FieldNames[]
	authpassportkod?: string
}

interface ComponentProps<T> {
	item: T
	form: WrappedFormUtils
	validatePassport?: boolean
	initialValue?: string
	lockedFields?: FieldNames[]
}

const actions = {
	getSuggestFmsUnit: fmsSlice.actions.getAll,
	clearFmsUnits: fmsSlice.actions.clearAll,
}

export const AuthPassportkodInput = <T extends BaseItem>({
	form,
	item,
	validatePassport,
	initialValue,
	lockedFields,
}: ComponentProps<T>) => {
	const suggestFmsUnitData = useSelector(fmsSelectors.getAll)
	const { getSuggestFmsUnit, clearFmsUnits } = useAppDispatch(actions)
	// eslint-disable-next-line @typescript-eslint/no-unused-vars
	const [_, set] = useState({})
	const { locked_fields = [], required_fields = [], hidden_fields = [] } = item
	const authPassportVal = form.getFieldValue(FieldNames.AUTHPASSPORT)
	const isAuthPassportDefault = isAuthPassport(authPassportVal)
	const authPassportKodVal = form.getFieldValue(FieldNames.AUTHPASSPORTKOD) ?? ''
	const authPassportKodValCleared = authPassportKodVal?.replace(
		new RegExp(`( |${formatProps.mask})`, 'g'),
		''
	)
	const isValidKod = authPassportKodValCleared.length === formatProps.format.length
	const suggestFmsUnitResults =
		authPassportKodValCleared.length && isValidKod && isAuthPassportDefault
			? suggestFmsUnitData.map((item) => item.result)
			: []
	const prevPassportValue = useRef(null)

	const getFmsUnits = () => {
		prevPassportValue.current = authPassportKodVal
		getSuggestFmsUnit({
			params: {
				origin: INITIATOR_REQUEST_RBIDOS_PAID,
				query: authPassportKodVal,
			},
		})
	}

	useEffect(() => {
		if (
			isAuthPassportDefault &&
			isValidKod &&
			form.isFieldTouched(FieldNames.AUTHPASSPORTKOD) &&
			prevPassportValue.current !== authPassportKodVal
		) {
			getFmsUnits()
		}
	}, [authPassportKodVal])

	const getUnitsOnFocus = () => {
		if (isValidKod && prevPassportValue.current !== authPassportKodVal) {
			getFmsUnits()
		}
	}

	useEffectOnce(() => {
		return () => clearFmsUnits()
	})

	const onSelect = (val: string) => {
		form.setFieldsValue({
			[FieldNames.AUTHPASSPORTKOD]: authPassportKodVal,
			[FieldNames.AUTHPASSPORTWHOGIVE]: val,
		})
	}

	const hasError = Boolean(form.getFieldError(FieldNames.AUTHPASSPORTKOD))

	return (
		<BgFormRowElement
			fields={required_fields}
			fieldName={FieldNames.AUTHPASSPORTKOD}
			lockedFields={lockedFields || locked_fields}
			hiddenFields={hidden_fields}
			hideFormat={getPassportDataText(initialValue, 'code')}
			label="Код подразделения"
			lockedElem={initialValue}
			size={UISizes.MEDIUM}
			fixWidth
		>
			<AuthPassportKodWrapper hasError={hasError}>
				<BgSelectWithOther
					form={form}
					onFocus={() => getUnitsOnFocus()}
					lockedFields={lockedFields || locked_fields}
					fieldName={FieldNames.AUTHPASSPORTKOD}
					items={suggestFmsUnitResults}
					fieldOptions={{
						rules: [
							{
								validator: Validators.checkRequired({
									required: required_fields.includes(FieldNames.AUTHPASSPORTKOD),
								}),
							},
						],
						initialValue: initialValue || undefined,
					}}
					onSelectItem={onSelect}
					{...(validatePassport && formatProps)}
					skipFilterItems
					// TODO убрать после обновления react-number-format
					// @ts-ignore
					onChange={() => set({})}
				/>
			</AuthPassportKodWrapper>
		</BgFormRowElement>
	)
}
